import {MedicinalProductDto, PrescriptionDraft, PrescriptionSetDto} from '../types/prescription';
import {AppointmentDetailsForPrescriptionDto} from '../types/appointments';
import {SelectedPrescription} from '../components/prescription/PrescriptionItem/PrescriptionItem';
import {TFunction} from 'react-i18next';
import { SelectedPrescriptionDraft } from '../components/prescription/PrescriptionDraftItem/PrescriptionDraftItem';
import { v4 } from 'uuid';

export const mapMedicinalProductForAutocomplete = (
    medicinalProduct: MedicinalProductDto,
) => ({...medicinalProduct, value: medicinalProduct.id});

export const mapAppointmentDetailsToPrescription = (
    appointmentDetails: AppointmentDetailsForPrescriptionDto,
) => {
    const {id, patient, medic} = appointmentDetails;
    const patientDetails = appointmentDetails.child || patient;
    return {
        appointmentId: id,
        patientId: patient.userId,
        medicId: medic.userId,
        patientName: patientDetails.firstName,
        patientSurname: patientDetails.surname,
        patientPesel: patientDetails.pesel,
        effectiveDateTime: new Date().toISOString(),
        patientHomeAddress: patient.homeAddress,
        medicNPWZ: medic.npwz,
        medicName: medic.firstName,
        medicSurname: medic.surname,
        medicBusinessRole: medic.businessRole,
        medicTitle: medic.title,
        medicSpecialties: medic.specialties,
        representedMedicId: medic.representedMedicId,
        medicalAssistantName: medic.medicalAssistantFirstName,
        medicalAssistantSurname: medic.medicalAssistantSurname,
        medicalAssistantPesel: medic.medicalAssistantPesel
    };
};

export const mapPrescriptionToIssuePrescription = (
    p: SelectedPrescription,
    order: number,
) => ({
    order: order + 1,
    quantity: p.quantity,
    effectiveDate: new Date(p.effectiveDate).toISOString().split('T')[0],
    medicinalProductId: p.medicinalProduct.id,
    medicationPackId: p.medicationPack.id,
    treatmentDuration: p.treatmentDuration ?? null,
    dosageInterval: p.dosageInterval ?? null,
    dosageIntervalType: p.dosageIntervalType ?? null,
    dosageAmount: p.dosageAmount ?? null,
    dosageUnit: p.dosageUnit ?? null,
    usageRecommendation: p.usageRecommendation,
    paymentLevel: p.reimbursement?.paymentLevel,
});

export const getPrescriptionSetFileName = (
    prescriptionSet: PrescriptionSetDto,
    t: TFunction,
) => {
    const effectiveDateTime = new Date(prescriptionSet.effectiveDateTime);
    const date = effectiveDateTime.toLocaleDateString();
    const time = effectiveDateTime.toLocaleTimeString();
    return `${t('prescription')}_${
        prescriptionSet.patientPesel
    }_${date}_${time}.pdf`;
};

export const mapPrescriptionToSendPrescriptionDraft = (
    p: SelectedPrescriptionDraft,
    order: number,
) => ({
    order: order + 1,
    quantity: p.quantity,
    medicinalProductId: p.medicinalProduct.id,
    medicationPackId: p.medicationPack.id,
});

export const mapPrescriptionDraftToSelectedPrescription = (p: PrescriptionDraft): SelectedPrescription => ({
    medicinalProduct: p.medicinalProduct,
    medicationPack: p.medicationPack,
    quantity: p.quantity,
    treatmentDuration: null,
    dosageInterval: null,
    dosageIntervalType: null,
    dosageAmount: null,
    dosageUnit: null,
    usageRecommendation: "",
    effectiveDate: "",
    reimbursement: null,
    id: v4(),
})
    
const hasStructuredDosageData = (prescription: SelectedPrescription) =>
    prescription.treatmentDuration != null ||
    prescription.dosageInterval != null ||
    prescription.dosageIntervalType != null ||
    prescription.dosageAmount != null ||
    Boolean(prescription.dosageUnit);

export const isPrescriptionFilled = (prescription: SelectedPrescription) => 
    prescription.medicinalProduct && 
    prescription.medicationPack && 
    prescription.quantity && 
    prescription.effectiveDate && 
    (hasStructuredDosageData(prescription) || Boolean(prescription.usageRecommendation?.trim()));